import { scaleColor, type LinearRGB } from '../core/color';
import { clamp } from '../core/math';
import type { SkyStop } from './types';
import { skyColor } from './atmosphere';
import { OPEN_WATER, openWaterAlbedo } from './openWater';

/*
 * TypeScript mirror of the water albedo in water.frag (STYLE §7, §12.11), before the open water's
 * shift (openWaterPatches.ts), the shore band, the brush texture and the fog.
 *
 * The painter's open water is a fit to the painting (openWater.ts): a ramp from its steep water to its
 * shallow water across 48 to 40.1 deg of grazing angle, far steeper than Fresnel's. It is fixed to the
 * grazing angle, so in any view that looks down on the water it would draw a ring about the viewer, a
 * dark disc inside, at a radius close to the viewer's height. Away from the painter view every kind of
 * water follows one physical curve instead: its body, the open water's darkened by the round-7 fit's
 * factor (OPEN_WATER.freeBaseScale), mirrors the
 * clear sky at the reflected elevation by Schlick's approximation of water's reflectance, which rises
 * smoothly from straight down to the horizon; ripples break the mirror into the streaks a painted and
 * a real canal show. The painter amount blends the fit into the curve, so the painter view keeps its
 * water exactly. The painting has no water under 28 deg to fit, and it supports no other cue: in the
 * painter view the bank beside each canal and river is within 1.3 L* of the water's edge and from
 * 2.9 L* darker to 1.9 L* lighter than the bank behind it, and where its water strokes have a
 * direction (the Amstel, coherence 0.73) they cross the flow.
 */
export const WATER_CUE = {
  /** Reflectance of water (n = 1.33) seen straight down. */
  normalReflectance: 0.02,
  /** Cell of the ripple noise, the water brush's own cell. */
  rippleCellM: 3,
  /** How far one ripple of noise amplitude 1 moves the reflection about its mean. */
  rippleGain: 0.6,
} as const;

export interface ClearSky {
  readonly stops: readonly SkyStop[];
  readonly hazeColor: LinearRGB;
}

const DEG = Math.PI / 180;

/** Schlick's reflectance of water at a view this many degrees above its surface. */
export function waterReflectance(grazingDeg: number): number {
  const cosIncidence = Math.sin(Math.max(grazingDeg, 0) * DEG);
  const f0 = WATER_CUE.normalReflectance;
  return f0 + (1 - f0) * Math.pow(1 - cosIncidence, 5);
}

export function mixColor(a: LinearRGB, b: LinearRGB, t: number): LinearRGB {
  return [a[0] * (1 - t) + b[0] * t, a[1] * (1 - t) + b[1] * t, a[2] * (1 - t) + b[2] * t];
}

function paintedWaterAlbedo(
  body: LinearRGB,
  view: readonly [number, number, number],
  kindCode: number
): LinearRGB {
  return kindCode < OPEN_WATER.kindLimit ? openWaterAlbedo(view) : body;
}

/** Water away from the painter view, its ripples averaged out and its waves (waves.ts) lying level. */
export function freeWaterAlbedo(
  body: LinearRGB,
  grazingDeg: number,
  kindCode: number,
  sky: ClearSky
): LinearRGB {
  const base = kindCode < OPEN_WATER.kindLimit ? scaleColor(body, OPEN_WATER.freeBaseScale) : body;
  const mirrored = skyColor(sky.stops, sky.hazeColor, grazingDeg);
  return mixColor(base, mirrored, waterReflectance(grazingDeg));
}

/** The albedo water.frag gives water of this kind code seen along this view. */
export function waterAlbedo(
  body: LinearRGB,
  view: readonly [number, number, number],
  kindCode: number,
  painterAmount: number,
  sky: ClearSky
): LinearRGB {
  const painted = paintedWaterAlbedo(body, view, kindCode);
  if (painterAmount >= 1) return painted;
  const grazingDeg = Math.asin(clamp(-view[1] / Math.hypot(...view), -1, 1)) / DEG;
  return mixColor(freeWaterAlbedo(body, grazingDeg, kindCode, sky), painted, painterAmount);
}
